import { useParams } from "react-router";
import {
  EventIdPublic,
  EventIdPublicOrSlug,
  EventSlug,
  type EventParticipantIdPublic,
  type UserIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import type { EventView } from "@frontend/shared/features/actions/application/actions/use-event-actions";
import { UIComponentContextError } from "@planta-v2/pds-ui/errors/ui-component.errors";
import { useAppNavigation, type AppNavigation } from "./useAppNavigation";
import { useEventSubdomain } from "./useEventSubdomain";

const EVENT_ROUTE_CONTEXT_COMPONENT_NAME = "useRequiredEventRouteContext";
const EVENT_ROUTE_CONTEXT_REQUIRED_CONTEXT = "an event-scoped route";

export interface CurrentParticipantResult {
  readonly participantIdPublic: EventParticipantIdPublic | null;
  readonly userIdPublic: UserIdPublic | null;
  readonly isolated: boolean | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

export interface EventRouteContext {
  readonly eventIdPublicOrSlug: string;
  readonly event: EventView | undefined;
  readonly eventIdPublic: EventIdPublic;
  readonly eventSlug: EventSlug | null;
  readonly currentParticipant: CurrentParticipantResult;
  readonly nav: AppNavigation;
  readonly isEventSubdomain: boolean;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Composes the event-scoped route context from the URL + `useEventActions`,
 * replacing the deleted `EventContextProvider`/`EventContext` (data/nav concerns;
 * the URL-redirect/locale side-effects now live in `EventRouteLayout`). Returns
 * `null` when the current route resolves to no event (empty id/slug) — mirroring
 * the old `useContext(EventContext)` returning `null` outside the provider.
 */
export function useEventRouteContext(): EventRouteContext | null {
  const { eventIdPublicOrSlug: param } = useParams<{
    eventIdPublicOrSlug: string;
  }>();
  const subdomain = useEventSubdomain();
  const eventIdPublicOrSlug = param ?? subdomain ?? "";

  const userActions = useUserActions();
  const eventActions = userActions.useForEvent({
    eventIdPublicOrSlug: EventIdPublicOrSlug.unsafeCreate(eventIdPublicOrSlug),
  });

  const event = eventActions.event;
  const eventIdPublic =
    event?.idPublic ?? EventIdPublic.unsafeCreate(eventIdPublicOrSlug);
  const eventSlug = event !== undefined ? (event.slug ?? null) : null;
  const nav = useAppNavigation(eventSlug);

  const participantActions = eventActions.useForParticipant({ self: true });
  const participant = participantActions.participant;
  const currentParticipant: CurrentParticipantResult = {
    participantIdPublic: participant?.idPublic ?? null,
    userIdPublic: userActions.user?.idPublic ?? null,
    isolated: participant?.isolated ?? null,
    isLoading: participantActions.isLoading,
    error: participantActions.error,
  };

  if (eventIdPublicOrSlug === "") {
    return null;
  }

  return {
    eventIdPublicOrSlug,
    event,
    eventIdPublic,
    eventSlug,
    currentParticipant,
    nav,
    isEventSubdomain: subdomain !== null,
    isLoading: eventActions.isLoading,
    error: eventActions.error,
  };
}

/**
 * Same as {@link useEventRouteContext} but asserts an event route is present
 * (drop-in for the removed `useEventContext`, which threw when used outside the
 * event-scoped route tree). Use in controllers that only render under an event.
 */
export function useRequiredEventRouteContext(): EventRouteContext {
  const context = useEventRouteContext();
  if (context === null) {
    throw new UIComponentContextError(
      EVENT_ROUTE_CONTEXT_COMPONENT_NAME,
      EVENT_ROUTE_CONTEXT_REQUIRED_CONTEXT,
    );
  }
  return context;
}
